import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import client, { getErrorMessage, getBlobErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DocumentActionRow } from "@/components/DocumentActionRow";

const ASSET_DOC_HAS_EXPIRY = {
  invoice: false,
  insurance: true,
  puc: true,
  tax: true,
  inter_state_tax: true,
};

// Document Delete is scoped more tightly than the rest of Asset Register
// CRUD (Director/Manager/Accounts/Back Office) — Accounts can view but not
// delete a document, per the Document View & Delete Fix plan.
const DOC_DELETE_ROLES = ["Director", "Manager", "Back Office"];

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">{label}</p>
      <p className="text-sm text-slate-800 font-medium mt-0.5">{value || "—"}</p>
    </div>
  );
}

function fmtDate(v) {
  if (!v) return null;
  return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AssetDetailPanel({ asset: a, siteMap, onClose, onEdit, canManage, onUpdated }) {
  const { user } = useAuth();
  const canDeleteDoc = DOC_DELETE_ROLES.includes(user?.role);
  const [uploading, setUploading] = useState(null);
  const [busyDoc, setBusyDoc] = useState(null);
  const [expiryDrafts, setExpiryDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [addlLabel, setAddlLabel] = useState("");

  const refresh = async () => {
    const res = await client.get(`/assets/${a.asset_id}`);
    onUpdated(res.data);
  };

  const uploadDoc = async (docType, file) => {
    const needsExpiry = ASSET_DOC_HAS_EXPIRY[docType];
    const expiry = expiryDrafts[docType];
    if (needsExpiry && !expiry) {
      toast.error("An expiry date is required for this document");
      return;
    }
    setUploading(docType);
    const fd = new FormData();
    fd.append("file", file);
    if (expiry) fd.append("expiry_date", expiry);
    try {
      await client.post(`/assets/${a.asset_id}/documents/${docType}`, fd);
      toast.success("Document uploaded");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const viewDocument = async (docType) => {
    try {
      const res = await client.get(`/assets/${a.asset_id}/documents/${docType}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(await getBlobErrorMessage(err));
    }
  };

  const deleteDocument = async (docType) => {
    if (!window.confirm("Delete this document? It will be removed from the list.")) return;
    setBusyDoc(docType);
    try {
      await client.delete(`/assets/${a.asset_id}/documents/${docType}`);
      toast.success("Document deleted");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyDoc(null);
    }
  };

  const viewAdditionalDoc = async (docId) => {
    try {
      const res = await client.get(`/assets/${a.asset_id}/additional-documents/${docId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(await getBlobErrorMessage(err));
    }
  };

  const deleteAdditionalDoc = async (docId) => {
    if (!window.confirm("Delete this document? It will be removed from the list.")) return;
    setBusyDoc(docId);
    try {
      await client.delete(`/assets/${a.asset_id}/additional-documents/${docId}`);
      toast.success("Document deleted");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyDoc(null);
    }
  };

  const uploadAdditional = async (file) => {
    if (!addlLabel.trim()) return toast.error("Label is required for additional documents");
    setUploading("additional");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", addlLabel.trim());
    try {
      await client.post(`/assets/${a.asset_id}/additional-documents`, fd);
      toast.success("Additional document uploaded");
      setAddlLabel("");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      const path = a.active === false ? "reactivate" : "deactivate";
      const res = await client.post(`/assets/${a.asset_id}/${path}`);
      toast.success(a.active === false ? "Asset reactivated" : "Asset deactivated");
      onUpdated(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="asset-detail-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {a.machine_type}
            <Badge variant="outline" className="font-mono">
              {a.asset_id}
            </Badge>
          </SheetTitle>
          <SheetDescription>
            {a.brand} {a.capacity ? `· ${a.capacity}` : ""}
          </SheetDescription>
          {canManage && (
            <div className="flex gap-2 pt-2">
              <Button size="sm" data-testid="asset-detail-edit-button" onClick={() => onEdit(a)} className="bg-amber-600 hover:bg-amber-700">
                Edit
              </Button>
              <Button size="sm" variant="outline" disabled={busy} data-testid="asset-detail-toggle-active-button" onClick={toggleActive}>
                {a.active === false ? "Reactivate" : "Deactivate"}
              </Button>
            </div>
          )}
        </SheetHeader>

        <Tabs defaultValue="identity" className="mt-4">
          <TabsList>
            <TabsTrigger value="identity" data-testid="asset-tab-identity">
              Identity
            </TabsTrigger>
            <TabsTrigger value="assignment" data-testid="asset-tab-assignment">
              Assignment
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="asset-tab-documents">
              Documents
            </TabsTrigger>
          </TabsList>

          <TabsContent value="identity" className="grid grid-cols-2 gap-4 pt-4">
            <Field label="Asset ID" value={a.asset_id} />
            <Field label="Machine Type" value={a.machine_type} />
            <Field label="Brand" value={a.brand} />
            <Field label="Capacity" value={a.capacity} />
            <Field label="Serial Number" value={a.serial_number} />
            <Field label="Engine Number" value={a.engine_number} />
            <Field label="Purchase Date" value={fmtDate(a.purchase_date)} />
            <div className="col-span-2">
              <Field label="Remarks" value={a.remarks} />
            </div>
          </TabsContent>

          <TabsContent value="assignment" className="grid grid-cols-2 gap-4 pt-4">
            <Field label="Owned By Entity" value={a.owned_by_entity} />
            <Field label="Current Status" value={a.current_status} />
            <Field label="Assigned Site" value={siteMap[a.assigned_site] || a.assigned_site} />
            <Field label="Active" value={a.active === false ? "Deactivated" : "Yes"} />
          </TabsContent>

          <TabsContent value="documents" className="pt-4 space-y-2">
            {Object.entries(a.documents || {}).map(([key, doc]) => (
              <div key={key} className="rounded-md border border-slate-200 px-3 py-2 space-y-1.5">
                {doc.uploaded ? (
                  <DocumentActionRow
                    testId={`asset-doc-${key}`}
                    label={key.replace(/_/g, " ")}
                    filename={doc.filename}
                    meta={doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString("en-IN") : null}
                    canDelete={canDeleteDoc}
                    busy={busyDoc === key}
                    onView={() => viewDocument(key)}
                    onDelete={() => deleteDocument(key)}
                  />
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-sm capitalize">{key.replace(/_/g, " ")}</span>
                    <Badge variant="outline" className="text-slate-400">
                      Missing
                    </Badge>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  {canManage && ASSET_DOC_HAS_EXPIRY[key] && (
                    <Input
                      type="date"
                      data-testid={`asset-doc-expiry-${key}`}
                      className="h-8 text-xs max-w-[180px]"
                      placeholder="Expiry date (required)"
                      value={expiryDrafts[key] || ""}
                      onChange={(ev) => setExpiryDrafts((d) => ({ ...d, [key]: ev.target.value }))}
                    />
                  )}
                  {canManage && (
                    <label
                      className={`inline-flex items-center gap-1.5 text-xs cursor-pointer ${uploading === key ? "text-slate-300" : "text-amber-600 hover:text-amber-700"}`}
                      data-testid={`asset-doc-upload-${key}`}
                    >
                      <Upload className="h-3.5 w-3.5" /> {doc.uploaded ? "Replace" : "Upload"}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png"
                        disabled={uploading === key}
                        onChange={(ev) => ev.target.files[0] && uploadDoc(key, ev.target.files[0])}
                      />
                    </label>
                  )}
                </div>
              </div>
            ))}

            {(a.additional_documents || []).length > 0 && (
              <div className="pt-2 space-y-1.5">
                <p className="text-xs uppercase tracking-wide text-slate-400 font-mono mb-2">Additional Documents</p>
                {a.additional_documents.map((d) => (
                  <DocumentActionRow
                    key={d.doc_id}
                    testId={`asset-additional-doc-${d.doc_id}`}
                    label={d.label}
                    filename={d.filename}
                    meta={d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("en-IN") : null}
                    canDelete={canDeleteDoc}
                    busy={busyDoc === d.doc_id}
                    onView={() => viewAdditionalDoc(d.doc_id)}
                    onDelete={() => deleteAdditionalDoc(d.doc_id)}
                  />
                ))}
              </div>
            )}

            {canManage && (
              <div className="pt-2 rounded-md border border-dashed border-slate-300 px-3 py-2 space-y-2">
                <p className="text-xs font-medium text-slate-600">Add Additional Document</p>
                <Input
                  data-testid="asset-additional-doc-label-input"
                  placeholder="Label (required)"
                  value={addlLabel}
                  onChange={(e) => setAddlLabel(e.target.value)}
                  className="h-8 text-xs"
                />
                <label
                  className={`inline-flex items-center gap-1.5 text-xs cursor-pointer ${uploading === "additional" ? "text-slate-300" : "text-amber-600 hover:text-amber-700"}`}
                  data-testid="asset-additional-doc-upload"
                >
                  <Upload className="h-3.5 w-3.5" /> Upload file
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    disabled={uploading === "additional"}
                    onChange={(ev) => ev.target.files[0] && uploadAdditional(ev.target.files[0])}
                  />
                </label>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
